import React, { useState, useEffect, useContext } from 'react';
import { Input, Card, Empty, Skeleton, Select, message, App, Modal, Button, List } from 'antd';
import { ShoppingOutlined } from '@ant-design/icons';
import { getUserLocation, getCuisineByLocation, getRecipesByCuisine, searchRecipes, getSpoonacularRecipeDetails } from '../services/recipeService';
import AppContext from '../context/AppContext';

const { Search } = Input;
const { Option } = Select;

const cuisines = [
  'Indian', 'Chinese', 'Italian', 'Mexican', 'American', 'Thai',
  'Japanese', 'French', 'Mediterranean', 'Korean', 'Vietnamese'
];

const RecipeSearch = () => {
  const { dispatch } = useContext(AppContext);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    const initializeRecipes = async () => {
      try {
        const location = await getUserLocation();
        if (location) {
          const localCuisine = getCuisineByLocation(location.country);
          setSelectedCuisine(localCuisine);
          const localRecipes = await getRecipesByCuisine(localCuisine);
          setRecipes(localRecipes);
        }
      } catch (error) {
        console.error('Error initializing recipes:', error);
        message.error('Failed to load recipes');
      } finally {
        setLoading(false);
      }
    };

    initializeRecipes();
  }, []);

  const handleSearch = async (value) => {
    if (!value.trim()) return;
    
    setLoading(true);
    try {
      const results = await searchRecipes(value);
      setRecipes(results);
    } catch (error) {
      console.error('Error searching recipes:', error);
      message.error('Failed to search recipes');
    } finally {
      setLoading(false);
    }
  };

  const handleCuisineChange = async (cuisine) => {
    setSelectedCuisine(cuisine);
    setLoading(true);
    try {
      const results = await getRecipesByCuisine(cuisine);
      setRecipes(results);
    } catch (error) {
      console.error('Error loading cuisine recipes:', error);
      message.error('Failed to load cuisine recipes');
    } finally {
      setLoading(false);
    }
  };

  const handleRecipeClick = async (recipe) => {
    setModalVisible(true);
    
    if (recipe.source === 'Spoonacular' && recipe.needsDetails) {
      setSelectedRecipe({ ...recipe, loading: true });
      try {
        const detailedRecipe = await getSpoonacularRecipeDetails(recipe.id);
        setSelectedRecipe(detailedRecipe);
      } catch (error) {
        message.error('Failed to load recipe details');
        setModalVisible(false);
      }
    } else {
      setSelectedRecipe(recipe);
    }
  };

  const handleAddToList = (recipe) => {
    recipe.ingredients.forEach(ingredient => {
      dispatch({
        type: 'ADD_ITEM',
        payload: {
          id: `${Date.now()}-${Math.random()}`,
          name: ingredient.name,
          measure: ingredient.measure,
          mealName: recipe.title
        }
      });
    });
    message.success('Added ingredients to shopping list');
    setModalVisible(false);
  };

  return (
    <App>
      <div style={{ padding: '16px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Search
            placeholder="Search for recipes..."
            onSearch={handleSearch}
            style={{ marginBottom: '16px' }}
            size="large"
          />
          <Select
            value={selectedCuisine}
            onChange={handleCuisineChange}
            style={{ width: '100%' }}
            placeholder="Select cuisine"
          >
            {cuisines.map(cuisine => (
              <Option key={cuisine} value={cuisine}>{cuisine}</Option>
            ))}
          </Select>
        </div>

        {loading ? (
          <div style={{ padding: '24px' }}>
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
          </div>
        ) : recipes.length === 0 ? (
          <Empty description="No recipes found" />
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            {recipes.map(recipe => (
              <Card
                key={recipe.id}
                hoverable
                onClick={() => handleRecipeClick(recipe)}
                cover={
                  <img 
                    alt={recipe.title}
                    src={recipe.image}
                    style={{ height: 200, objectFit: 'cover' }}
                  />
                }
                style={{ height: '100%' }}
              >
                <Card.Meta
                  title={recipe.title}
                  description={
                    <>
                      <div>Cuisine: {recipe.cuisine}</div>
                      <div>Category: {recipe.category}</div>
                      <div>Source: {recipe.source}</div>
                    </>
                  }
                />
              </Card>
            ))}
          </div>
        )}

        <Modal
          title={selectedRecipe?.title}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setModalVisible(false)}>
              Close
            </Button>,
            <Button
              key="add"
              type="primary"
              icon={<ShoppingOutlined />}
              onClick={() => handleAddToList(selectedRecipe)}
              disabled={selectedRecipe?.loading}
            >
              Add to Shopping List
            </Button>
          ]}
          width={800}
        >
          {selectedRecipe?.loading ? (
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <Skeleton active />
            </div>
          ) : selectedRecipe && (
            <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
              <img 
                src={selectedRecipe.image} 
                alt={selectedRecipe.title}
                style={{ 
                  width: '100%', 
                  maxHeight: '300px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}
              />
              
              <h3>Ingredients:</h3>
              <List
                dataSource={selectedRecipe.ingredients}
                renderItem={item => (
                  <List.Item>
                    <strong>{item.name}:</strong> {item.measure}
                  </List.Item>
                )}
                style={{ marginBottom: '16px' }}
              />

              <h3>Instructions:</h3>
              <p style={{ whiteSpace: 'pre-line' }}>{selectedRecipe.instructions}</p>

              <div style={{ marginTop: '16px' }}>
                <strong>Category:</strong> {selectedRecipe.category}<br />
                <strong>Cuisine:</strong> {selectedRecipe.cuisine}<br />
                <strong>Source:</strong> {selectedRecipe.source}
              </div>
            </div>
          )}
        </Modal>
      </div>
    </App>
  );
};

export default RecipeSearch; 